import React, { useState, useMemo } from "react";
import {
  DollarSign,
  TrendingUp,
  Building2,
  PieChart as PieIcon,
  BarChart3,
  Search,
  ArrowUpRight,
  CheckCircle2,
  Boxes,
  Calendar,
  X,
  Filter,
  Check,
  ChevronDown,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const CHART_COLORS = [
  "#7c3aed",
  "#2563eb",
  "#059669",
  "#d97706",
  "#dc2626",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
];

const TIER_COLORS = {
  "High (> ₹10L)": "#7c3aed",
  "Medium (₹1L - ₹10L)": "#2563eb",
  "Low (< ₹1L)": "#10b981",
};

const formatINR = (val) => {
  if (val === null || val === undefined || isNaN(val)) return "₹0";
  const num = Number(val);
  const abs = Math.abs(num);
  if (abs >= 1_00_00_000) return `₹${(num / 1_00_00_000).toFixed(2)} Cr`;
  if (abs >= 1_00_000) return `₹${(num / 1_00_000).toFixed(2)} L`;
  if (abs >= 1_000) return `₹${(num / 1_000).toFixed(1)} K`;
  return `₹${num.toLocaleString("en-IN")}`;
};

const formatExactINR = (val) => {
  if (val === null || val === undefined || isNaN(val)) return "₹0";
  return Number(val).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  });
};

const PurchaseValueAnalytics = ({ data, onExport }) => {
  const [selectedPlant, setSelectedPlant] = useState("all");
  const [selectedSupplier, setSelectedSupplier] = useState("all");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState("all");
  const [selectedDatePreset, setSelectedDatePreset] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showGraphs, setShowGraphs] = useState(false);

  const rawOrders = useMemo(() => data?.purchase_orders || [], [data]);

  // Comprehensive master suppliers list (1,200+ suppliers from SAP)
  const masterSuppliers = useMemo(() => {
    if (data?.suppliers && data.suppliers.length > 0) {
      return data.suppliers;
    }
    const map = {};
    rawOrders.forEach((po) => {
      const id = po.supplier_id || po.vendor || "";
      const name = po.vendor_name || po.vendor || id;
      if (id && !map[id]) {
        map[id] = {
          supplier_id: id,
          supplier_name: name,
          display_name: `${id} - ${name}`,
        };
      }
    });
    return Object.values(map).sort((a, b) => (a.supplier_name || "").localeCompare(b.supplier_name || ""));
  }, [data?.suppliers, rawOrders]);

  // Filtered suppliers for the searchable combobox (instant slice for max performance)
  const filteredSuppliersList = useMemo(() => {
    if (!supplierSearch.trim()) return masterSuppliers.slice(0, 80);
    const q = supplierSearch.toLowerCase();
    return masterSuppliers
      .filter((s) => s.supplier_id.toLowerCase().includes(q) || s.supplier_name.toLowerCase().includes(q))
      .slice(0, 80);
  }, [masterSuppliers, supplierSearch]);

  const selectedSupplierObj = useMemo(() => {
    if (selectedSupplier === "all") return null;
    return masterSuppliers.find((s) => s.supplier_id === selectedSupplier || s.supplier_name === selectedSupplier);
  }, [masterSuppliers, selectedSupplier]);

  // Check if any filter or search is active (Option C requirement: empty by default until filtered)
  const hasFiltersActive = useMemo(() => {
    return (
      selectedPlant !== "all" ||
      selectedSupplier !== "all" ||
      selectedTier !== "all" ||
      (selectedDatePreset !== "all" && selectedDatePreset !== "") ||
      Boolean(selectedDate) ||
      searchTerm.trim() !== ""
    );
  }, [selectedPlant, selectedSupplier, selectedTier, selectedDatePreset, selectedDate, searchTerm]);

  // Unique plants for dropdown
  const plants = useMemo(() => {
    const set = new Set(rawOrders.map((po) => po.plant || "Plant 1000").filter(Boolean));
    return Array.from(set);
  }, [rawOrders]);

  // Rolling Last 6 Calendar Days
  const last6Days = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 6; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      dates.push(d.toISOString().slice(0, 10));
    }
    return dates;
  }, []);

  // Date benchmark strings
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const yesterdayStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  }, []);
  const thirtyDaysAgoStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);

  // Filtered dataset: only computes when a filter is applied
  const filteredOrders = useMemo(() => {
    if (!hasFiltersActive) return [];

    return rawOrders.filter((po) => {
      // 1. Plant filter
      const matchPlant = selectedPlant === "all" || (po.plant || "Plant 1000") === selectedPlant;

      // 2. Supplier filter
      const vName = po.vendor_name || po.vendor || "";
      const sId = po.supplier_id || "";
      const matchSupplier =
        selectedSupplier === "all" ||
        sId === selectedSupplier ||
        vName === selectedSupplier;

      // 3. Tier filter
      const val = Number(po.net_value || 0);
      let matchTier = true;
      if (selectedTier === "high") matchTier = val >= 10_00_000;
      else if (selectedTier === "medium") matchTier = val >= 1_00_000 && val < 10_00_000;
      else if (selectedTier === "low") matchTier = val < 1_00_000;

      // 4. Creation Date filter
      const poDate = (po.creation_date || "").slice(0, 10);
      let matchDate = true;
      if (selectedDatePreset === "today") {
        matchDate = poDate === todayStr;
      } else if (selectedDatePreset === "yesterday") {
        matchDate = poDate === yesterdayStr;
      } else if (selectedDatePreset === "last6") {
        matchDate = poDate >= last6Days[5] && poDate <= last6Days[0];
      } else if (selectedDatePreset === "last30") {
        matchDate = poDate >= thirtyDaysAgoStr;
      } else if (selectedDatePreset === "custom" || selectedDate) {
        matchDate = !selectedDate || poDate === selectedDate;
      }

      // 5. Search query
      const q = searchTerm.toLowerCase();
      const matchSearch =
        !searchTerm ||
        (po.po_number && po.po_number.toLowerCase().includes(q)) ||
        (po.material && po.material.toLowerCase().includes(q)) ||
        (po.material_desc && po.material_desc.toLowerCase().includes(q)) ||
        (po.vendor && po.vendor.toLowerCase().includes(q)) ||
        (po.vendor_name && po.vendor_name.toLowerCase().includes(q));

      return matchPlant && matchSupplier && matchTier && matchDate && matchSearch;
    });
  }, [
    rawOrders,
    hasFiltersActive,
    selectedPlant,
    selectedSupplier,
    selectedTier,
    selectedDatePreset,
    selectedDate,
    searchTerm,
    todayStr,
    yesterdayStr,
    last6Days,
    thirtyDaysAgoStr,
  ]);

  // Dataset for Top KPI Cards (shows full current year 2026 data by default, or filtered data when filter is active)
  const displayOrders = useMemo(() => {
    return hasFiltersActive ? filteredOrders : rawOrders;
  }, [hasFiltersActive, filteredOrders, rawOrders]);

  // Computed metrics for Top KPI Cards
  const totalDisplayValue = useMemo(() => {
    return displayOrders.reduce((sum, po) => sum + Number(po.net_value || 0), 0);
  }, [displayOrders]);

  const totalDisplayOrdersCount = useMemo(() => {
    const set = new Set(displayOrders.map((po) => po.po_number).filter(Boolean));
    return set.size || displayOrders.length;
  }, [displayOrders]);

  const avgDisplayOrderValue = useMemo(() => {
    return totalDisplayOrdersCount ? totalDisplayValue / totalDisplayOrdersCount : 0;
  }, [totalDisplayValue, totalDisplayOrdersCount]);

  const openValue = useMemo(() => {
    return displayOrders
      .filter((po) => {
        const s = String(po.status || "").toLowerCase();
        return !s || s.includes("open") || s.includes("process") || s.includes("pending") || s.includes("unavailable");
      })
      .reduce((sum, po) => sum + Number(po.net_value || 0), 0);
  }, [displayOrders]);

  const completedValue = useMemo(() => {
    return displayOrders
      .filter((po) => {
        const s = String(po.status || "").toLowerCase();
        return s.includes("complete") || s.includes("deliver") || s.includes("approved");
      })
      .reduce((sum, po) => sum + Number(po.net_value || 0), 0);
  }, [displayOrders]);

  const completedPct = totalDisplayValue
    ? Math.min(100, Math.round((completedValue / totalDisplayValue) * 100))
    : 0;

  // Monthly / Daily trend chart data
  const trendData = useMemo(() => {
    const map = {};
    displayOrders.forEach((po) => {
      const dt = (po.creation_date || "").slice(0, 10);
      const key = selectedDatePreset === "custom" || selectedDate ? dt : dt.slice(0, 7) || "Recent";
      if (!map[key]) map[key] = { month: key, date: dt, spend: 0, count: 0 };
      map[key].spend += Number(po.net_value || 0);
      map[key].count += 1;
    });
    const arr = Object.values(map);
    arr.sort((a, b) => a.month.localeCompare(b.month));
    return arr;
  }, [displayOrders, selectedDatePreset, selectedDate]);

  // Category chart data
  const categoryData = useMemo(() => {
    const map = {};
    displayOrders.forEach((po) => {
      const c = po.category || "General";
      if (!map[c]) map[c] = { category: c, total_spend: 0, count: 0 };
      map[c].total_spend += Number(po.net_value || 0);
      map[c].count += 1;
    });
    const arr = Object.values(map);
    arr.sort((a, b) => b.total_spend - a.total_spend);
    return arr.slice(0, 8);
  }, [displayOrders]);

  // Plant chart data
  const plantData = useMemo(() => {
    if (!hasFiltersActive) return [];
    const map = {};
    filteredOrders.forEach((po) => {
      const p = po.plant || "Plant 1000";
      if (!map[p]) map[p] = { plant: p, total_spend: 0, count: 0 };
      map[p].total_spend += Number(po.net_value || 0);
      map[p].count += 1;
    });
    const arr = Object.values(map);
    arr.sort((a, b) => b.total_spend - a.total_spend);
    return arr;
  }, [filteredOrders, hasFiltersActive]);

  // Value tier chart data
  const tierData = useMemo(() => {
    if (!hasFiltersActive) return [];
    const tiers = {
      "High (> ₹10L)": { tier: "High (> ₹10L)", total_spend: 0, count: 0, color: "#7c3aed" },
      "Medium (₹1L - ₹10L)": {
        tier: "Medium (₹1L - ₹10L)",
        total_spend: 0,
        count: 0,
        color: "#2563eb",
      },
      "Low (< ₹1L)": { tier: "Low (< ₹1L)", total_spend: 0, count: 0, color: "#10b981" },
    };
    filteredOrders.forEach((po) => {
      const v = Number(po.net_value || 0);
      if (v >= 10_00_000) {
        tiers["High (> ₹10L)"].total_spend += v;
        tiers["High (> ₹10L)"].count += 1;
      } else if (v >= 1_00_000) {
        tiers["Medium (₹1L - ₹10L)"].total_spend += v;
        tiers["Medium (₹1L - ₹10L)"].count += 1;
      } else {
        tiers["Low (< ₹1L)"].total_spend += v;
        tiers["Low (< ₹1L)"].count += 1;
      }
    });
    return Object.values(tiers).filter((t) => t.count > 0);
  }, [filteredOrders, hasFiltersActive]);

  // Top materials table
  const topMaterials = useMemo(() => {
    if (!hasFiltersActive) return [];
    const map = {};
    filteredOrders.forEach((po) => {
      const m = po.material || "Unknown";
      if (!map[m]) {
        map[m] = {
          material: m,
          description: po.material_desc || po.description || m,
          category: po.category || "General",
          total_spend: 0,
          total_qty: 0,
          unit: po.unit || "EA",
          count: 0,
        };
      }
      map[m].total_spend += Number(po.net_value || 0);
      map[m].total_qty += Number(po.quantity || 0);
      map[m].count += 1;
    });
    const arr = Object.values(map);
    arr.sort((a, b) => b.total_spend - a.total_spend);
    return arr.slice(0, 10);
  }, [filteredOrders, hasFiltersActive]);

  // Top vendors
  const topVendors = useMemo(() => {
    if (!hasFiltersActive) return [];
    const map = {};
    filteredOrders.forEach((po) => {
      const v = po.vendor_name || po.vendor || po.supplier_id || "Unknown";
      const id = po.supplier_id || po.vendor_id || "—";
      if (!map[v]) {
        map[v] = {
          vendor: v,
          supplier_id: id,
          category: po.category || "General",
          total_spend: 0,
          count: 0,
        };
      }
      map[v].total_spend += Number(po.net_value || 0);
      map[v].count += 1;
    });
    const arr = Object.values(map);
    arr.sort((a, b) => b.total_spend - a.total_spend);
    return arr.slice(0, 5);
  }, [filteredOrders, hasFiltersActive]);

  const handleResetFilters = () => {
    setSelectedPlant("all");
    setSelectedSupplier("all");
    setSupplierSearch("");
    setSelectedTier("all");
    setSelectedDatePreset("all");
    setSelectedDate("");
    setSearchTerm("");
  };

  const handleDatePresetChange = (val) => {
    setSelectedDatePreset(val);
    if (val === "today") setSelectedDate(todayStr);
    else if (val === "yesterday") setSelectedDate(yesterdayStr);
    else if (val === "all") setSelectedDate("");
  };

  return (
    <div className="space-y-6" data-testid="pu-purchase-value-analytics">
      {/* Top Value KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Purchase Value */}
        <Card className="card-industrial border-l-4 border-l-violet-600 bg-gradient-to-br from-violet-50/40 via-card to-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                Total Purchase Value
              </span>
              <div className="p-2 bg-violet-100 rounded-lg text-violet-700">
                <DollarSign className="w-4 h-4" />
              </div>
            </div>
            <p className="text-2xl font-bold tracking-tight text-violet-950">
              {formatINR(totalDisplayValue)}
            </p>
            <div className="flex items-center justify-between text-xs text-muted-foreground mt-2 pt-2 border-t border-muted/50">
              <span>{displayOrders.length} line items</span>
              <Badge variant="outline" className="text-[10px] text-violet-700 bg-violet-50">
                {hasFiltersActive ? (selectedDate || selectedDatePreset !== "all" ? "Filtered Date" : "Filtered") : "2026 Base"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Average Order Value */}
        <Card className="card-industrial border-l-4 border-l-blue-600 bg-gradient-to-br from-blue-50/40 via-card to-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                Avg Value / Order
              </span>
              <div className="p-2 bg-blue-100 rounded-lg text-blue-700">
                <TrendingUp className="w-4 h-4" />
              </div>
            </div>
            <p className="text-2xl font-bold tracking-tight text-blue-950">
              {formatINR(avgDisplayOrderValue)}
            </p>
            <div className="flex items-center justify-between text-xs text-muted-foreground mt-2 pt-2 border-t border-muted/50">
              <span>{totalDisplayOrdersCount} distinct orders</span>
              <span className="text-blue-600 font-medium flex items-center text-[11px]">
                {hasFiltersActive ? "Calculated" : "High Volume"}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Delivered vs Open Value */}
        <Card className="card-industrial border-l-4 border-l-emerald-600 bg-gradient-to-br from-emerald-50/40 via-card to-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                Order Value Status
              </span>
              <div className="p-2 bg-emerald-100 rounded-lg text-emerald-700">
                <CheckCircle2 className="w-4 h-4" />
              </div>
            </div>
            <div className="flex items-baseline justify-between">
              <p className="text-2xl font-bold tracking-tight text-emerald-950">
                {openValue > 0 && completedValue === 0 ? formatINR(openValue) : formatINR(completedValue)}
              </p>
              <Badge variant="secondary" className="text-[10px] text-emerald-800 bg-emerald-100">
                {openValue > 0 && completedValue === 0 ? "Pending Delivery" : `${completedPct}% Delivered`}
              </Badge>
            </div>
            <div className="w-full bg-emerald-100 h-1.5 rounded-full overflow-hidden mt-2">
              <div
                className="bg-emerald-600 h-full rounded-full transition-all duration-500"
                style={{ width: `${completedPct || 100}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground mt-1.5">
              <span>Open: {formatINR(openValue)}</span>
              <span>Delivered: {formatINR(completedValue)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Card 4: Top Category */}
        <Card className="card-industrial border-l-4 border-l-amber-500 bg-gradient-to-br from-amber-50/40 via-card to-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                Top Spending Category
              </span>
              <div className="p-2 bg-amber-100 rounded-lg text-amber-700">
                <Boxes className="w-4 h-4" />
              </div>
            </div>
            <p className="text-xl font-bold tracking-tight text-amber-950 truncate">
              {categoryData[0]?.category || "—"}
            </p>
            <div className="flex items-center justify-between text-xs text-muted-foreground mt-2 pt-2 border-t border-muted/50">
              <span>{formatINR(categoryData[0]?.total_spend || 0)} spend</span>
              <Badge variant="outline" className="text-[10px] text-amber-700 bg-amber-50">
                {categoryData[0]?.count || 0} PO lines
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter & Search Toolbar */}
      <Card className="card-industrial">
        <CardContent className="p-3.5 space-y-3">
          {/* Row 1: Filter Controls */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Search */}
            <div className="relative flex-1 min-w-[170px] max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Search material, vendor, PO#..."
                className="pl-8 h-8 text-xs"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="pu-pv-search"
              />
            </div>

            {/* Date Filter Preset & Direct Picker */}
            <div className="flex items-center gap-1 bg-muted/40 p-0.5 rounded-md border shrink-0">
              <Calendar className="w-3.5 h-3.5 ml-1.5 text-violet-600 shrink-0" />
              <Select value={selectedDatePreset} onValueChange={handleDatePresetChange}>
                <SelectTrigger className="w-[115px] h-7 text-xs border-0 bg-transparent shadow-none" data-testid="pu-pv-date-preset">
                  <SelectValue placeholder="Select Date" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Dates</SelectItem>
                  <SelectItem value="today">Created Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="last6">Last 6 Days</SelectItem>
                  <SelectItem value="last30">Last 30 Days</SelectItem>
                  <SelectItem value="custom">Specific Day</SelectItem>
                </SelectContent>
              </Select>

              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setSelectedDatePreset("custom");
                }}
                className="w-[125px] h-7 text-xs font-medium border-0 bg-background shadow-xs px-1.5"
                title="Pick creation day"
                data-testid="pu-pv-date-input"
              />

              {selectedDate && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setSelectedDate("");
                    setSelectedDatePreset("all");
                  }}
                  title="Clear date"
                >
                  <X className="w-3 h-3" />
                </Button>
              )}
            </div>

            {/* Plant Filter */}
            <Select value={selectedPlant} onValueChange={setSelectedPlant}>
              <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="pu-pv-plant-filter">
                <SelectValue placeholder="All Plants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plants</SelectItem>
                {plants.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Searchable Supplier Popover (All 1,208 SAP Master Suppliers) */}
            <Popover open={supplierOpen} onOpenChange={setSupplierOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 min-w-[140px] max-w-[210px] justify-between text-xs font-normal border-input bg-background"
                  data-testid="pu-pv-supplier-filter"
                >
                  <span className="truncate">
                    {selectedSupplierObj ? selectedSupplierObj.supplier_name || selectedSupplierObj.display_name : "All Suppliers"}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-2 z-50 shadow-lg border bg-background" align="start">
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search 1,200+ suppliers by ID or name..."
                      className="pl-7 h-7 text-xs"
                      value={supplierSearch}
                      onChange={(e) => setSupplierSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="text-[10px] text-muted-foreground flex justify-between px-1 border-b pb-1">
                    <span>{supplierSearch ? `Matching results` : `Showing top 80 suppliers`}</span>
                    <span>Total: {masterSuppliers.length}</span>
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-0.5 pr-1 text-xs">
                    <button
                      onClick={() => {
                        setSelectedSupplier("all");
                        setSupplierOpen(false);
                        setSupplierSearch("");
                      }}
                      className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center justify-between cursor-pointer ${
                        selectedSupplier === "all" ? "bg-violet-600 text-white font-semibold" : "hover:bg-muted text-foreground"
                      }`}
                    >
                      <span>All Suppliers ({masterSuppliers.length})</span>
                      {selectedSupplier === "all" && <Check className="w-3.5 h-3.5 shrink-0" />}
                    </button>

                    {filteredSuppliersList.map((s) => {
                      const isSelected = selectedSupplier === s.supplier_id || selectedSupplier === s.supplier_name;
                      return (
                        <button
                          key={s.supplier_id}
                          onClick={() => {
                            setSelectedSupplier(s.supplier_id);
                            setSupplierOpen(false);
                          }}
                          className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center justify-between cursor-pointer ${
                            isSelected ? "bg-violet-600 text-white font-semibold" : "hover:bg-muted text-foreground"
                          }`}
                          title={s.display_name}
                        >
                          <div className="truncate pr-2">
                            <span className="font-mono text-[10px] opacity-70 block">{s.supplier_id}</span>
                            <span className="truncate block font-medium">{s.supplier_name}</span>
                          </div>
                          {isSelected && <Check className="w-3.5 h-3.5 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Value Tier Filter */}
            <Select value={selectedTier} onValueChange={setSelectedTier}>
              <SelectTrigger className="w-[115px] h-8 text-xs" data-testid="pu-pv-tier-filter">
                <SelectValue placeholder="All Tiers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="high">High (&gt; ₹10L)</SelectItem>
                <SelectItem value="medium">Medium (₹1L-₹10L)</SelectItem>
                <SelectItem value="low">Low (&lt; ₹1L)</SelectItem>
              </SelectContent>
            </Select>

            {hasFiltersActive && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-muted-foreground hover:text-foreground px-2"
                onClick={handleResetFilters}
                data-testid="pu-pv-reset-btn"
              >
                Reset Filters
              </Button>
            )}
          </div>

          {/* Row 2: Sub-bar with Last 6 Days Quick Buttons & Action Buttons */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2.5 border-t border-muted/50 text-xs">
            {/* Left: Last 6 Days Quick Buttons */}
            <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-muted-foreground">
              <span className="font-semibold text-foreground/85 flex items-center gap-1">
                <Calendar className="w-3 h-3 text-violet-600" />
                Last 6 Days:
              </span>
              {last6Days.map((d, idx) => {
                const label = idx === 0 ? `${d} (Today)` : idx === 1 ? `${d} (Yday)` : d;
                const isSelected = selectedDate === d;
                return (
                  <button
                    key={d}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedDate("");
                        setSelectedDatePreset("all");
                      } else {
                        setSelectedDate(d);
                        setSelectedDatePreset("custom");
                      }
                    }}
                    className={`px-2.5 py-0.5 rounded transition-all cursor-pointer font-mono text-[10px] ${
                      isSelected
                        ? "bg-violet-600 text-white font-bold shadow-xs scale-105"
                        : "bg-muted/80 hover:bg-violet-100 hover:text-violet-900 border border-muted-foreground/20"
                    }`}
                    title={`Filter purchase value created on ${d}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2.5 text-muted-foreground ml-auto">
              <Button
                variant={showGraphs ? "default" : "outline"}
                size="sm"
                className={`h-8 text-xs gap-1.5 shadow-xs font-medium ${
                  showGraphs
                    ? "bg-violet-600 hover:bg-violet-700 text-white border-violet-600"
                    : "border-border hover:bg-muted text-foreground"
                }`}
                onClick={() => setShowGraphs((prev) => !prev)}
                data-testid="pu-pv-toggle-graphs-btn"
              >
                <BarChart3 className="w-3.5 h-3.5" />
                {showGraphs ? "Hide Graphs" : "Show Graphs"}
              </Button>
              {onExport && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1.5 shadow-xs"
                  onClick={() => onExport("purchase_value")}
                  disabled={!hasFiltersActive}
                  data-testid="pu-pv-export-btn"
                >
                  Export Value Data
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Visual Analytics Charts Grid (2x2) - Visible when Show Graphs is toggled */}
      {showGraphs && (
        <>
          {!hasFiltersActive ? (
            <Card className="card-industrial border-dashed">
              <CardContent className="py-12 text-center text-xs text-muted-foreground space-y-2">
                <BarChart3 className="w-8 h-8 mx-auto text-muted-foreground/50" />
                <p className="font-semibold text-foreground">No Filter Selected for Graphs</p>
                <p>Select a date, plant, or category above to generate purchasing charts.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-in fade-in-50 duration-300">
              {/* Chart 1: Monthly/Daily Spend Trend */}
              <Card className="card-industrial">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4 text-violet-600" />
                        {selectedDate ? `Purchasing Value on ${selectedDate}` : "Purchasing Value Trend"}
                      </CardTitle>
                      <CardDescription className="text-xs">
                        {selectedDate
                          ? `Spend distribution across orders created on ${selectedDate}`
                          : "Procurement run-rate and spending pattern over time"}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="pvSpendGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#7c3aed" stopOpacity={0.0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => formatINR(v)}
                        width={65}
                      />
                      <Tooltip
                        formatter={(v) => [formatExactINR(v), "Purchasing Value"]}
                        labelFormatter={(l) => `Period / Date: ${l}`}
                      />
                      <Area
                        type="monotone"
                        dataKey="spend"
                        stroke="#7c3aed"
                        strokeWidth={2.5}
                        fillOpacity={1}
                        fill="url(#pvSpendGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Chart 2: Purchase Value by Category */}
              <Card className="card-industrial">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                        <BarChart3 className="w-4 h-4 text-blue-600" />
                        Purchase Value by Material Group
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Category spend allocation & volume distribution
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={categoryData} margin={{ top: 10, right: 10, left: 0, bottom: 25 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        dataKey="category"
                        tick={{ fontSize: 10 }}
                        angle={-20}
                        textAnchor="end"
                        height={45}
                      />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => formatINR(v)}
                        width={65}
                      />
                      <Tooltip
                        formatter={(v) => [formatExactINR(v), "Category Spend"]}
                      />
                      <Bar dataKey="total_spend" fill="#2563eb" radius={[4, 4, 0, 0]}>
                        {categoryData.map((_, idx) => (
                          <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Chart 3: Purchase Value by Plant */}
              <Card className="card-industrial">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 text-emerald-600" />
                        Purchase Value by Plant & Location
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Procurement spend across manufacturing units
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={plantData} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tickFormatter={(v) => formatINR(v)} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="plant" tick={{ fontSize: 11 }} width={85} />
                      <Tooltip formatter={(v) => [formatExactINR(v), "Plant Spend"]} />
                      <Bar dataKey="total_spend" fill="#059669" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Chart 4: Value Tier Distribution Donut */}
              <Card className="card-industrial">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                        <PieIcon className="w-4 h-4 text-amber-500" />
                        Purchase Order Value Tiers
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Distribution across High, Medium, and Low value orders
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={tierData}
                        dataKey="total_spend"
                        nameKey="tier"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        innerRadius={45}
                        paddingAngle={3}
                        label={({ tier, percent }) => `${tier.split(" ")[0]} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                        fontSize={11}
                      >
                        {tierData.map((entry, idx) => (
                          <Cell key={idx} fill={TIER_COLORS[entry.tier] || CHART_COLORS[idx % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => [formatExactINR(v), "Total Spend"]} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: "11px", paddingTop: "6px" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Top 10 Materials by Purchase Value Table */}
      <Card className="card-industrial">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Boxes className="w-4 h-4 text-violet-600" /> Top Materials by Purchase Value
              </CardTitle>
              <CardDescription className="text-xs">
                {hasFiltersActive
                  ? (selectedDate
                    ? `Highest spending materials created on ${selectedDate}`
                    : "Highest spending parts matching your active filter criteria")
                  : "Select a date, plant, category, or search term above to display matching materials"}
              </CardDescription>
            </div>
            {hasFiltersActive && (
              <Badge variant="secondary" className="text-xs font-normal">
                Showing {topMaterials.length} materials
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {!hasFiltersActive ? (
            <div className="text-center py-14 px-4 space-y-3">
              <div className="w-12 h-12 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center mx-auto shadow-xs">
                <Search className="w-5 h-5" />
              </div>
              <h4 className="text-sm font-semibold text-foreground">No Filter Selected</h4>
              <p className="text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
                Please click a date from the <b>Last 6 Days</b> bar, pick a <b>Plant</b>, <b>Category</b>, <b>Tier</b>, or type a <b>search query</b> above to display purchase value records.
              </p>
            </div>
          ) : topMaterials.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm space-y-2">
              <Filter className="w-8 h-8 mx-auto text-muted-foreground/40" />
              <p>No purchase orders found matching your selected filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    <th className="text-left p-3">Material Code</th>
                    <th className="text-left p-3">Description</th>
                    <th className="text-left p-3">Category</th>
                    <th className="text-right p-3">Total Qty</th>
                    <th className="text-right p-3">Total Value</th>
                    <th className="text-right p-3">Avg Unit Price</th>
                    <th className="text-right p-3">Share %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-muted/30">
                  {topMaterials.map((m, idx) => {
                    const share = totalDisplayValue
                      ? ((m.total_spend / totalDisplayValue) * 100).toFixed(1)
                      : "0.0";
                    return (
                      <tr key={idx} className="hover:bg-muted/30 transition-colors">
                        <td className="p-3 font-mono text-xs font-medium text-violet-700">
                          {m.material}
                        </td>
                        <td className="p-3 font-medium text-foreground">{m.description}</td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-xs font-normal">
                            {m.category}
                          </Badge>
                        </td>
                        <td className="p-3 text-right">
                          {m.total_qty.toLocaleString()} {m.unit}
                        </td>
                        <td className="p-3 text-right font-bold text-foreground">
                          {formatExactINR(m.total_spend)}
                        </td>
                        <td className="p-3 text-right text-muted-foreground">
                          {formatExactINR(m.avg_price || (m.total_qty ? m.total_spend / m.total_qty : 0))}
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <span className="text-xs font-medium">{share}%</span>
                            <div className="w-12 bg-muted h-1.5 rounded-full overflow-hidden hidden sm:block">
                              <div
                                className="bg-violet-600 h-full rounded-full"
                                style={{ width: `${Math.min(100, Number(share) * 2)}%` }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top 5 Suppliers by Value Cards */}
      <Card className="card-industrial">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Building2 className="w-4 h-4 text-violet-600" /> Top Suppliers by Purchase Value
          </CardTitle>
          <CardDescription className="text-xs">
            {hasFiltersActive
              ? (selectedDate
                ? `Suppliers with purchase orders created on ${selectedDate}`
                : "Key vendors receiving the largest share of purchase value for your selection")
              : "Select a filter above to view top supplier rankings"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!hasFiltersActive ? (
            <div className="text-center py-8 text-xs text-muted-foreground">
              Select a filter above to view top supplier rankings.
            </div>
          ) : topVendors.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground text-sm">
              No suppliers found for the selected filters.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {topVendors.map((v, idx) => {
                const share = totalDisplayValue
                  ? ((v.total_spend / totalDisplayValue) * 100).toFixed(1)
                  : "0.0";
                return (
                  <div
                    key={idx}
                    className="p-3.5 rounded-lg border bg-card hover:border-violet-300 transition-all shadow-xs"
                  >
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span className="font-mono text-[10px]">#{idx + 1}</span>
                      <Badge variant="secondary" className="text-[10px] py-0">
                        {v.count} POs
                      </Badge>
                    </div>
                    <h4 className="font-bold text-sm text-foreground truncate" title={v.vendor}>
                      {v.vendor}
                    </h4>
                    <p className="text-lg font-extrabold text-violet-700 mt-1">
                      {formatINR(v.total_spend)}
                    </p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mt-2 pt-2 border-t border-muted/40">
                      <span className="truncate">{v.category}</span>
                      <span className="font-semibold text-foreground">{share}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PurchaseValueAnalytics;
